// 戦闘エフェクト：fx.webp（4×4、Codex で生成した発光体。tools/art/fx-atlas.mjs）の部品を、コードで飛ばす・広げる・回す・散らす。
// キャラごとの攻撃と術、妖の爪・妖気の弾、回復、範囲ダメージのレシピ。加算合成（lighter）で光らせる。
// 位置は描き始めの盤面の座標（ピクセル）。時間は performance.now() 基準で、speed（倍速）で縮める。
import type { CharId } from '@hyakki/core';

export const FX_CELL = {
  fireball: 0,
  firework: 1,
  star: 2,
  windSlash: 3,
  whirl: 4,
  feather: 5,
  crack: 6,
  flash: 7,
  swordSlash: 8,
  blueFire: 9,
  spark: 10,
  ring: 11,
  claw: 12,
  orb: 13,
  heal: 14,
  gold: 15,
} as const;
export type FxSprite = keyof typeof FX_CELL;
const COLS = 4;

export interface Particle {
  sprite: FxSprite;
  /** 開始（performance.now）と長さ（ms） */
  t0: number;
  ms: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** 放物線の高さ（px、上向き） */
  arc?: number;
  /** 大きさ（px、一辺） */
  s0: number;
  s1: number;
  /** 回転（rad） */
  r0?: number;
  r1?: number;
  /** 進む向きに回す（飛び道具） */
  along?: boolean;
  /** 不透明度（既定 1 → 0）。fadeIn の割合までは 0 から上げる */
  a0?: number;
  a1?: number;
  fadeIn?: number;
  /** 縦のつぶれ（地面に置く輪・地割れ） */
  sy?: number;
  ease?: 'out' | 'in' | 'lin';
  /** 別の絵（奥義の ougi_<キャラ> など、4×4）。cell はその絵のセル番号（sprite の代わり） */
  sheet?: string;
  cell?: number;
  /** コマ送り：cell から flip コマを寿命の間に順に出す */
  flip?: number;
  /** 加算でなく普通に重ねる */
  normal?: boolean;
}

export type FxKind = CharId | 'claw' | 'orb';
export interface Pt {
  x: number;
  y: number;
}

/** 部品の大きさの全体倍率（ヘックスの半径に対して）。盤面で見て迫力が出る大きさ */
const G = 1.7;
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);
const easeIn = (t: number) => t * t;

/** 粒を描く。終わったものを除いた配列を返す */
type Img = CanvasImageSource & { width: number; height: number };
export function drawParticles(ctx: CanvasRenderingContext2D, base: Img | null, list: Particle[], now: number, imgOf?: (key: string) => Img | null): Particle[] {
  const keep: Particle[] = [];
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const p of list) {
    const raw = (now - p.t0) / p.ms;
    if (raw >= 1) continue;
    keep.push(p);
    if (raw < 0) continue;
    const t = p.ease === 'in' ? easeIn(raw) : p.ease === 'lin' ? raw : easeOut(raw);
    const x = p.x0 + (p.x1 - p.x0) * t;
    const y = p.y0 + (p.y1 - p.y0) * t - (p.arc ?? 0) * Math.sin(Math.PI * t);
    const size = p.s0 + (p.s1 - p.s0) * t;
    let rot = (p.r0 ?? 0) + ((p.r1 ?? p.r0 ?? 0) - (p.r0 ?? 0)) * t;
    if (p.along) {
      // 進む向き（放物線の接線）
      const dx = p.x1 - p.x0;
      const dy = p.y1 - p.y0 - (p.arc ?? 0) * Math.PI * Math.cos(Math.PI * t);
      rot += Math.atan2(dy, dx);
    }
    let a = (p.a0 ?? 1) + ((p.a1 ?? 0) - (p.a0 ?? 1)) * raw;
    if (p.fadeIn && raw < p.fadeIn) a *= raw / p.fadeIn;
    if (a <= 0.01 || size <= 0.5) continue;
    const img = p.sheet ? (imgOf?.(p.sheet) ?? null) : base;
    if (!img) continue;
    const cw = img.width / COLS;
    const ch = img.height / COLS;
    const i = p.sheet ? Math.min(15, (p.cell ?? 0) + (p.flip ? Math.min(p.flip - 1, Math.floor(raw * p.flip)) : 0)) : FX_CELL[p.sprite];
    ctx.globalCompositeOperation = p.normal ? 'source-over' : 'lighter';
    ctx.globalAlpha = Math.min(1, a);
    ctx.save();
    ctx.translate(x, y);
    if (p.sy) ctx.scale(1, p.sy);
    ctx.rotate(rot);
    ctx.drawImage(img, (i % COLS) * cw, Math.floor(i / COLS) * ch, cw, ch, -size / 2, -size / 2, size, size);
    ctx.restore();
  }
  ctx.restore();
  return keep;
}

/** 乱数（見た目だけ。戦闘の結果には関わらない） */
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

/**
 * 攻撃・術のエフェクト。a＝攻撃する側、b＝狙う所（どちらも体の中心）、s＝ヘックスの半径、k＝時間の倍率（1/speed）。
 * hitAt＝当たるまでの時間（この後にダメージの表示が続く）
 */
export function attackRecipe(kind: FxKind, skill: boolean, a: Pt, b: Pt, s: number, now: number, k: number): { parts: Particle[]; hitAt: number } {
  const parts: Particle[] = [];
  const dir = Math.atan2(b.y - a.y, b.x - a.x);
  const far = Math.hypot(b.x - a.x, b.y - a.y) > s * 2.4;
  const P = (p: Omit<Particle, 't0' | 'ms'> & { at?: number; ms: number }) => parts.push({ ...p, s0: p.s0 * G, s1: p.s1 * G, t0: now + (p.at ?? 0) * k, ms: p.ms * k });
  const at = (pt: Pt, extra: Partial<Particle> = {}) => ({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y, ...extra });
  const impact = (when: number, size = 1, sprite: FxSprite = 'spark') => {
    P({ sprite: 'flash', ...at(b), s0: s * 0.5 * size, s1: s * 1.5 * size, r0: rnd(0, 1), at: when, ms: 220, a0: 0.9 });
    P({ sprite, ...at(b), s0: s * 0.8 * size, s1: s * 1.9 * size, r0: rnd(0, 6), at: when, ms: 260, a0: 1 });
  };
  switch (kind) {
    case 'akari': {
      if (!skill) {
        // 狐火の玉：放物線で飛び、尾を引いて弾ける
        const fly = 280;
        P({ sprite: 'fireball', x0: a.x, y0: a.y, x1: b.x, y1: b.y, arc: s * 0.5, s0: s * 0.85, s1: s * 0.95, along: true, a0: 1, a1: 1, ms: fly, ease: 'lin' });
        for (let i = 1; i <= 5; i++) {
          const t = i / 6;
          P({ sprite: 'fireball', x0: a.x + (b.x - a.x) * t, y0: a.y + (b.y - a.y) * t - s * 0.5 * Math.sin(Math.PI * t), x1: a.x + (b.x - a.x) * t, y1: a.y + (b.y - a.y) * t - s * 0.5 * Math.sin(Math.PI * t) - s * 0.2, s0: s * 0.5, s1: s * 0.15, at: fly * t, ms: 220, a0: 0.6, r0: dir });
        }
        impact(fly, 0.9);
        return { parts, hitAt: fly };
      }
      // 花火：高く打ち上げて、大輪が開き、星が散って落ちる
      const fly = 340;
      P({ sprite: 'fireball', x0: a.x, y0: a.y, x1: b.x, y1: b.y - s * 0.4, arc: s * 1.4, s0: s * 0.7, s1: s * 0.5, along: true, a0: 1, a1: 1, ms: fly, ease: 'lin' });
      P({ sprite: 'firework', ...at({ x: b.x, y: b.y - s * 0.4 }), s0: s * 0.4, s1: s * 3.4, r0: rnd(0, 6), r1: rnd(0, 6) + 0.4, at: fly, ms: 700, a0: 1 });
      P({ sprite: 'ring', ...at(b), sy: 0.55, s0: s * 0.5, s1: s * 3.2, at: fly, ms: 520, a0: 0.8 });
      P({ sprite: 'flash', ...at({ x: b.x, y: b.y - s * 0.4 }), s0: s * 1, s1: s * 2.4, at: fly, ms: 260 });
      for (let i = 0; i < 12; i++) {
        const ang = (i / 12) * Math.PI * 2 + rnd(-0.2, 0.2);
        const r = s * rnd(1.4, 2.2);
        P({ sprite: i % 3 ? 'star' : 'gold', x0: b.x, y0: b.y - s * 0.4, x1: b.x + Math.cos(ang) * r, y1: b.y - s * 0.4 + Math.sin(ang) * r + s * 0.7, s0: s * 0.55, s1: s * 0.15, r0: 0, r1: rnd(-3, 3), at: fly + 40, ms: rnd(650, 850) });
      }
      return { parts, hitAt: fly + 20 };
    }
    case 'kazamaru': {
      if (!skill) {
        // 羽団扇の風の刃：交差する二筋と、舞う羽根
        P({ sprite: 'windSlash', ...at(b), s0: s * 1.4, s1: s * 1.9, r0: dir - 0.7, r1: dir + 0.5, ms: 260, a0: 1 });
        P({ sprite: 'windSlash', ...at(b), s0: s * 1.2, s1: s * 1.7, r0: dir + Math.PI + 0.7, r1: dir + Math.PI - 0.5, at: 70, ms: 260, a0: 0.9 });
        for (let i = 0; i < 3; i++) {
          const ang = dir + rnd(-1.2, 1.2);
          P({ sprite: 'feather', x0: b.x, y0: b.y, x1: b.x + Math.cos(ang) * s * 1.3, y1: b.y + Math.sin(ang) * s * 0.8 + s * 0.5, s0: s * 0.5, s1: s * 0.4, r0: rnd(0, 6), r1: rnd(-4, 4), at: 60, ms: 650, a0: 0.9 });
        }
        P({ sprite: 'spark', ...at(b), s0: s * 0.6, s1: s * 1.4, r0: rnd(0, 6), at: 40, ms: 200 });
        return { parts, hitAt: 110 };
      }
      // 疾風：つむじ風が走り、十字の風の刃と羽根の嵐
      const fly = 360;
      P({ sprite: 'whirl', x0: a.x, y0: a.y, x1: b.x, y1: b.y, s0: s * 1.1, s1: s * 1.8, r0: 0, r1: 0, ms: fly + 120, a0: 0.95, a1: 0.2, ease: 'lin' });
      P({ sprite: 'windSlash', ...at(b), s0: s * 1.6, s1: s * 2.4, r0: dir - 0.9, r1: dir + 0.7, at: fly - 40, ms: 320 });
      P({ sprite: 'windSlash', ...at(b), s0: s * 1.6, s1: s * 2.4, r0: dir + Math.PI / 2 - 0.9, r1: dir + Math.PI / 2 + 0.7, at: fly + 20, ms: 320 });
      for (let i = 0; i < 8; i++) {
        const ang = (i / 8) * Math.PI * 2;
        P({ sprite: 'feather', x0: b.x, y0: b.y, x1: b.x + Math.cos(ang) * s * 1.8, y1: b.y + Math.sin(ang) * s * 1.1 - s * 0.3, s0: s * 0.55, s1: s * 0.35, r0: rnd(0, 6), r1: rnd(-6, 6), at: fly, ms: 700 });
      }
      P({ sprite: 'ring', ...at(b), sy: 0.55, s0: s * 0.6, s1: s * 2.6, at: fly, ms: 420, a0: 0.6 });
      return { parts, hitAt: fly };
    }
    case 'suzune': {
      if (!skill) {
        // 大槌の一撃：閃光と火花、地面に衝撃の輪
        impact(0, 1.1);
        P({ sprite: 'ring', ...at({ x: b.x, y: b.y + s * 0.35 }), sy: 0.5, s0: s * 0.5, s1: s * 1.8, ms: 320, a0: 0.8 });
        return { parts, hitAt: 60 };
      }
      // 破城：地割れが走り、二重の衝撃波、金の破片が飛び散る
      P({ sprite: 'crack', ...at({ x: b.x, y: b.y + s * 0.35 }), sy: 0.55, s0: s * 0.8, s1: s * 2.8, r0: rnd(0, 6), ms: 800, a0: 1, fadeIn: 0.05 });
      P({ sprite: 'flash', ...at(b), s0: s * 1.2, s1: s * 3.2, ms: 280 });
      P({ sprite: 'ring', ...at({ x: b.x, y: b.y + s * 0.35 }), sy: 0.5, s0: s * 0.6, s1: s * 3.4, ms: 520, a0: 0.9 });
      P({ sprite: 'ring', ...at({ x: b.x, y: b.y + s * 0.35 }), sy: 0.5, s0: s * 0.4, s1: s * 2.6, at: 120, ms: 480, a0: 0.7 });
      for (let i = 0; i < 9; i++) {
        const ang = -Math.PI / 2 + rnd(-1.3, 1.3);
        const r = s * rnd(1, 2);
        P({ sprite: 'gold', x0: b.x, y0: b.y, x1: b.x + Math.cos(ang) * r, y1: b.y + s * 0.4, arc: s * rnd(0.6, 1.3), s0: s * 0.4, s1: s * 0.2, r0: rnd(0, 6), r1: rnd(-5, 5), at: 30, ms: rnd(550, 750), ease: 'lin' });
      }
      return { parts, hitAt: 90 };
    }
    case 'yuzuha': {
      if (!skill) {
        // 刀：離れていれば剣閃が飛ぶ。当たった所に大きな斬撃の弧
        let hit = 90;
        if (far) {
          hit = 220;
          P({ sprite: 'swordSlash', x0: a.x, y0: a.y, x1: b.x, y1: b.y, s0: s * 1, s1: s * 1.2, along: true, a0: 0.9, a1: 0.9, ms: hit, ease: 'lin' });
        }
        P({ sprite: 'swordSlash', ...at(b), s0: s * 1.5, s1: s * 2, r0: dir + 0.9, r1: dir - 0.3, at: hit - 60, ms: 260 });
        P({ sprite: 'spark', ...at(b), s0: s * 0.6, s1: s * 1.5, r0: rnd(0, 6), at: hit, ms: 220 });
        return { parts, hitAt: hit };
      }
      // 狐火斬り：十字の剣閃と青い狐火、立ちのぼる火の粉
      const hit = far ? 200 : 120;
      if (far) P({ sprite: 'blueFire', x0: a.x, y0: a.y, x1: b.x, y1: b.y, s0: s * 0.8, s1: s * 1, along: true, a0: 1, a1: 1, ms: hit, ease: 'lin' });
      P({ sprite: 'swordSlash', ...at(b), s0: s * 1.8, s1: s * 2.4, r0: dir + 0.9, r1: dir - 0.4, at: hit - 60, ms: 300 });
      P({ sprite: 'swordSlash', ...at(b), s0: s * 1.8, s1: s * 2.4, r0: dir - 0.9 + Math.PI, r1: dir + 0.4 + Math.PI, at: hit, ms: 300 });
      P({ sprite: 'blueFire', ...at(b), s0: s * 0.8, s1: s * 2.2, at: hit, ms: 520 });
      for (let i = 0; i < 6; i++) {
        const x = b.x + rnd(-s * 0.8, s * 0.8);
        P({ sprite: 'blueFire', x0: x, y0: b.y + rnd(-s * 0.2, s * 0.3), x1: x + rnd(-s * 0.3, s * 0.3), y1: b.y - s * rnd(1.2, 1.8), s0: s * 0.55, s1: s * 0.15, at: hit + i * 40, ms: 650 });
      }
      return { parts, hitAt: hit };
    }
    case 'orb': {
      // 妖気の弾
      const fly = 300;
      P({ sprite: 'orb', x0: a.x, y0: a.y, x1: b.x, y1: b.y, arc: s * 0.4, s0: s * 0.7, s1: s * 0.8, r0: 0, r1: 4, a0: 1, a1: 1, ms: fly, ease: 'lin' });
      P({ sprite: 'orb', ...at(b), s0: s * 0.6, s1: s * 1.6, at: fly, ms: 300 });
      P({ sprite: 'spark', ...at(b), s0: s * 0.6, s1: s * 1.4, r0: rnd(0, 6), at: fly, ms: 220 });
      return { parts, hitAt: fly };
    }
    default: {
      // 妖の爪（近接の敵・中立）
      P({ sprite: 'claw', ...at(b), s0: s * 1.1, s1: s * 1.6, r0: dir + 0.6, r1: dir + 0.9, ms: 280, a0: 1, fadeIn: 0.1 });
      P({ sprite: 'spark', ...at(b), s0: s * 0.5, s1: s * 1.3, r0: rnd(0, 6), at: 50, ms: 220, a0: 0.8 });
      return { parts, hitAt: 80 };
    }
  }
}

/** 回復：緑の十字のきらめきが立ちのぼる */
export function healRecipe(b: Pt, s: number, now: number, k: number): Particle[] {
  const parts: Particle[] = [];
  for (let i = 0; i < 4; i++) {
    const x = b.x + rnd(-s * 0.5, s * 0.5);
    parts.push({ sprite: 'heal', x0: x, y0: b.y + s * 0.2, x1: x, y1: b.y - s * 1.1, s0: s * 0.6 * G, s1: s * 0.3 * G, t0: now + i * 70 * k, ms: 620 * k, a0: 1, fadeIn: 0.15 });
  }
  parts.push({ sprite: 'ring', x0: b.x, y0: b.y + s * 0.35, x1: b.x, y1: b.y + s * 0.35, sy: 0.5, s0: s * 0.5 * G, s1: s * 1.8 * G, t0: now, ms: 480 * k, a0: 0.5 });
  return parts;
}

/** 範囲ダメージ（術の巻き添え）：小さな花火と火花 */
export function splashRecipe(b: Pt, s: number, now: number, k: number): Particle[] {
  return [
    { sprite: 'firework', x0: b.x, y0: b.y, x1: b.x, y1: b.y, s0: s * 0.3 * G, s1: s * 1.6 * G, r0: rnd(0, 6), t0: now, ms: 480 * k, a0: 0.9 },
    { sprite: 'spark', x0: b.x, y0: b.y, x1: b.x, y1: b.y, s0: s * 0.5 * G, s1: s * 1.3 * G, r0: rnd(0, 6), t0: now, ms: 260 * k, a0: 0.9 },
  ];
}

const scaled = (list: Particle[]) => list.map((p) => ({ ...p, s0: p.s0 * G, s1: p.s1 * G }));
const still = (b: Pt) => ({ x0: b.x, y0: b.y, x1: b.x, y1: b.y });

/** 撃破：白い閃光と衝撃の輪、魂の光の粒が立ちのぼる。big＝ボス・宿敵（大きく、星も散る） */
export function koRecipe(b: Pt, s: number, now: number, k: number, big = false): Particle[] {
  const m = big ? 1.5 : 1;
  const out: Particle[] = [
    { sprite: 'flash', ...still(b), s0: s * 0.8 * m, s1: s * 2.2 * m, r0: rnd(0, 1), t0: now, ms: 300 * k, a0: 1 },
    { sprite: 'ring', x0: b.x, y0: b.y + s * 0.35, x1: b.x, y1: b.y + s * 0.35, sy: 0.5, s0: s * 0.5, s1: s * 2.4 * m, t0: now, ms: 460 * k, a0: 0.9 },
    { sprite: 'spark', ...still(b), s0: s * 0.9 * m, s1: s * 2 * m, r0: rnd(0, 6), t0: now, ms: 280 * k, a0: 1 },
  ];
  const n = big ? 12 : 6;
  for (let i = 0; i < n; i++) {
    const x = b.x + rnd(-s * 0.6, s * 0.6) * m;
    out.push({ sprite: 'gold', x0: x, y0: b.y + rnd(-s * 0.2, s * 0.3), x1: x + rnd(-s * 0.3, s * 0.3), y1: b.y - s * rnd(1.2, 2.2) * m, s0: s * 0.45, s1: s * 0.12, t0: now + rnd(0, 160) * k, ms: rnd(650, 900) * k, a0: 0.95, fadeIn: 0.1 });
  }
  if (big)
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2;
      out.push({ sprite: 'star', x0: b.x, y0: b.y, x1: b.x + Math.cos(ang) * s * 2.2, y1: b.y + Math.sin(ang) * s * 1.4, s0: s * 0.6, s1: s * 0.15, r1: 2, t0: now + 60 * k, ms: 620 * k });
    }
  return scaled(out);
}

/** 本陣への攻撃：地割れと閃光。fall＝攻め落とした（大爆発） */
export function coreRecipe(b: Pt, s: number, now: number, k: number, fall = false): Particle[] {
  const base: Particle[] = [
    { sprite: 'crack', x0: b.x, y0: b.y + s * 0.4, x1: b.x, y1: b.y + s * 0.4, sy: 0.55, s0: s * 0.8, s1: s * 1.9, r0: rnd(0, 6), t0: now, ms: 520 * k, a0: 0.9, fadeIn: 0.05 },
    { sprite: 'flash', ...still(b), s0: s * 0.7, s1: s * 1.8, t0: now, ms: 240 * k },
  ];
  if (!fall) return scaled(base);
  base.push(
    { sprite: 'firework', ...still({ x: b.x, y: b.y - s * 0.6 }), s0: s * 0.6, s1: s * 4.2, r0: rnd(0, 6), t0: now + 80 * k, ms: 900 * k },
    { sprite: 'flash', ...still(b), s0: s * 1.5, s1: s * 4.5, t0: now + 60 * k, ms: 420 * k },
    { sprite: 'ring', x0: b.x, y0: b.y + s * 0.4, x1: b.x, y1: b.y + s * 0.4, sy: 0.5, s0: s * 0.8, s1: s * 5, t0: now + 60 * k, ms: 700 * k },
    { sprite: 'ring', x0: b.x, y0: b.y + s * 0.4, x1: b.x, y1: b.y + s * 0.4, sy: 0.5, s0: s * 0.5, s1: s * 3.6, t0: now + 260 * k, ms: 620 * k, a0: 0.7 },
  );
  for (let i = 0; i < 16; i++) {
    const ang = rnd(0, Math.PI * 2);
    const r = s * rnd(1.4, 3.2);
    base.push({ sprite: i % 2 ? 'gold' : 'star', x0: b.x, y0: b.y - s * 0.4, x1: b.x + Math.cos(ang) * r, y1: b.y + Math.sin(ang) * r * 0.7 + s * 0.6, arc: s * rnd(0.5, 1.2), s0: s * 0.55, s1: s * 0.15, r1: rnd(-4, 4), t0: now + rnd(60, 220) * k, ms: rnd(800, 1100) * k, ease: 'lin' });
  }
  return scaled(base);
}

/** 龍脈の占領：陣営の光の輪と、金の粒が舞い上がる */
export function captureRecipe(b: Pt, s: number, now: number, k: number): Particle[] {
  const out: Particle[] = [
    { sprite: 'ring', x0: b.x, y0: b.y + s * 0.35, x1: b.x, y1: b.y + s * 0.35, sy: 0.5, s0: s * 0.4, s1: s * 2, t0: now, ms: 520 * k, a0: 0.9 },
    { sprite: 'star', ...still({ x: b.x, y: b.y - s * 0.6 }), s0: s * 0.4, s1: s * 1.4, r1: 1.5, t0: now, ms: 420 * k },
  ];
  for (let i = 0; i < 6; i++) {
    const x = b.x + rnd(-s * 0.5, s * 0.5);
    out.push({ sprite: 'gold', x0: x, y0: b.y + s * 0.3, x1: x, y1: b.y - s * rnd(0.8, 1.4), s0: s * 0.35, s1: s * 0.1, t0: now + i * 50 * k, ms: 600 * k, fadeIn: 0.15 });
  }
  return scaled(out);
}

/** 建築：地面の輪と火花 */
export function buildRecipe(b: Pt, s: number, now: number, k: number): Particle[] {
  return scaled([
    { sprite: 'ring', x0: b.x, y0: b.y + s * 0.35, x1: b.x, y1: b.y + s * 0.35, sy: 0.5, s0: s * 0.4, s1: s * 1.6, t0: now, ms: 420 * k, a0: 0.7 },
    { sprite: 'spark', ...still(b), s0: s * 0.5, s1: s * 1.1, r0: rnd(0, 6), t0: now, ms: 260 * k, a0: 0.8 },
  ]);
}

/** 本陣が崩れるときの連鎖する小爆発（n 回、少しずつ場所と時間をずらす） */
export function chainBlastRecipe(b: Pt, s: number, now: number, k: number, n = 5): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < n; i++) {
    const at = now + i * 200 * k + rnd(0, 60) * k;
    const c = { x: b.x + rnd(-s * 0.8, s * 0.8), y: b.y + rnd(-s * 0.7, s * 0.4) };
    out.push(
      { sprite: 'flash', ...still(c), s0: s * 0.5, s1: s * 1.6, r0: rnd(0, 1), t0: at, ms: 240 * k },
      { sprite: 'fireball', ...still(c), s0: s * 0.6, s1: s * 1.4, r0: rnd(0, 6), t0: at, ms: 380 * k, a0: 0.9 },
      { sprite: 'spark', ...still(c), s0: s * 0.6, s1: s * 1.5, r0: rnd(0, 6), t0: at, ms: 260 * k },
    );
  }
  return scaled(out);
}


// ---------- 妖力の奥義（ougi_<キャラ>：4×4、0〜7＝本体のコマ送り、8〜15＝部品。tools/art/ougi-atlas.mjs） ----------

/** 奥義の絵のセル（部品）。art-src/gen-ougi.sh の並び */
const OG = { main: 0, p1: 8, p2: 9, p3: 10, p4: 11, p5: 12, p6: 13, p7: 14, p8: 15 };

/**
 * 奥義のエフェクト。a＝放つ側、b＝狙う所、s＝ヘックスの半径、k＝時間の倍率（1/speed）。
 * 通常の fx.webp の部品（閃光・輪など）も重ねる。hitAt＝当たるまでの時間
 */
export function ougiRecipe(kind: CharId | 'shuten', a: Pt, b: Pt, s: number, now: number, k: number): { parts: Particle[]; hitAt: number } {
  const parts: Particle[] = [];
  const sheet = `ougi_${kind}`;
  const dir = Math.atan2(b.y - a.y, b.x - a.x);
  const P = (p: Omit<Particle, 't0' | 'ms'> & { at?: number; ms: number }) => parts.push({ ...p, t0: now + (p.at ?? 0) * k, ms: p.ms * k });
  const O = (cell: number, p: Omit<Particle, 't0' | 'ms' | 'sprite' | 'sheet' | 'cell'> & { at?: number; ms: number }) => P({ sprite: 'flash', sheet, cell, ...p });
  const at = (pt: Pt, extra: Partial<Particle> = {}) => ({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y, ...extra });
  // 溜め：放つ側に光が集まる
  P({ sprite: 'ring', ...at(a), s0: s * 3.2, s1: s * 0.4, at: 0, ms: 260, a0: 0.2, a1: 1, ease: 'in' });
  P({ sprite: 'flash', ...at(a), s0: s * 0.6, s1: s * 2.2, at: 160, ms: 240, a0: 1 });
  let hitAt = 300;
  const burst = (when: number, size: number) => {
    O(OG.main, { ...at(b), s0: s * size, s1: s * size * 1.15, flip: 8, at: when, ms: 1000, a0: 1, a1: 0.85, ease: 'lin' });
    P({ sprite: 'flash', ...at(b), s0: s * 1.5, s1: s * 6, at: when, ms: 320, a0: 1 });
    P({ sprite: 'ring', ...at(b), s0: s * 1, s1: s * 7, at: when + 40, ms: 520, a0: 0.9, sy: 0.6 });
  };
  const spray = (cell: number, n: number, when: number, dist: number, size: number, ms: number, from: Pt = b, spin = true) => {
    for (let i = 0; i < n; i++) {
      const t = (i / n) * Math.PI * 2 + rnd(-0.2, 0.2);
      const d = dist * rnd(0.7, 1.15);
      O(cell, { x0: from.x, y0: from.y, x1: from.x + Math.cos(t) * d, y1: from.y + Math.sin(t) * d * 0.8, s0: s * size, s1: s * size * 0.5, r0: spin ? rnd(0, 6) : t, r1: spin ? rnd(0, 6) + 3 : t, at: when + rnd(0, 80), ms, a0: 1 });
    }
  };
  if (kind === 'akari') {
    // 大輪百花：花火玉が昇って大輪が開き、周りにも小さな花火が咲く
    O(OG.p1, { x0: a.x, y0: a.y, x1: b.x, y1: b.y, arc: s * 2.5, s0: s * 1.6, s1: s * 2.2, along: true, r0: Math.PI / 2, at: 220, ms: 360, a0: 1, a1: 0.8, ease: 'in' });
    hitAt = 580;
    burst(560, 10);
    spray(OG.p2, 12, 600, s * 4, 1.4, 700);
    spray(OG.p4, 10, 640, s * 3.2, 0.9, 760);
    spray(OG.p8, 6, 700, s * 2.5, 2.2, 900);
    for (let i = 0; i < 6; i++) {
      const t = (i / 6) * Math.PI * 2;
      const c = { x: b.x + Math.cos(t) * s * 1.75, y: b.y + Math.sin(t) * s * 1.5 };
      O(OG.p3, { ...at(c), s0: s * 0.6, s1: s * 2.6, r0: rnd(0, 6), at: 700 + i * 50, ms: 520, a0: 1 });
    }
    O(OG.p5, { ...at(b), s0: s * 2, s1: s * 8, at: 600, ms: 600, a0: 0.9, sy: 0.7 });
    for (let i = 0; i < 5; i++) O(OG.p7, { x0: b.x + rnd(-s * 2.5, s * 2.5), y0: b.y - s * 1.5, x1: b.x + rnd(-s * 2.5, s * 2.5), y1: b.y + s * 2, s0: s * 2, s1: s * 2.4, at: 900 + i * 60, ms: 700, a0: 0.8 });
  } else if (kind === 'kazamaru') {
    // 神風：風の刃が一直線に走り、竜巻が巻き起こって奥まで貫く
    const far = { x: b.x + Math.cos(dir) * s * 3.4, y: b.y + Math.sin(dir) * s * 3.4 };
    for (let i = 0; i < 3; i++) O(OG.p1, { x0: a.x, y0: a.y, x1: far.x, y1: far.y, s0: s * 2.4, s1: s * 3.2, r0: dir, at: 200 + i * 70, ms: 380, a0: 1, a1: 0.3, ease: 'lin' });
    for (let i = 0; i < 6; i++) O(OG.p5, { x0: a.x, y0: a.y, x1: far.x, y1: far.y, s0: s * 2.6, s1: s * 3, r0: dir, at: 180 + i * 40, ms: 300, a0: 0.7, a1: 0, ease: 'lin' });
    hitAt = 360;
    O(OG.main, { ...at(b), s0: s * 9.5, s1: s * 10.5, r0: dir, flip: 8, at: 340, ms: 1000, a0: 1, a1: 0.8, ease: 'lin' });
    P({ sprite: 'flash', ...at(b), s0: s * 1.5, s1: s * 5, at: 340, ms: 300, a0: 1 });
    O(OG.p4, { ...at(b), s0: s * 1, s1: s * 7, at: 380, ms: 560, a0: 0.9, sy: 0.6 });
    spray(OG.p2, 14, 400, s * 4.5, 1.1, 900);
    spray(OG.p6, 8, 420, s * 3.5, 1, 800);
    O(OG.p3, { ...at(far), s0: s * 1, s1: s * 3.5, at: 520, ms: 600, a0: 1 });
    O(OG.p7, { ...at(b), s0: s * 1, s1: s * 5, r0: dir, at: 360, ms: 300, a0: 1 });
    spray(OG.p8, 5, 450, s * 3, 2, 800);
  } else if (kind === 'shuten') {
    // 百鬼覇道（プロローグの酒呑童子）：紅蓮の斬撃が斜めに走り、炎が爆ぜて鬼の角の火柱が立ち、火の粉に散る
    hitAt = 300;
    O(OG.p1, { x0: b.x - s * 2.2, y0: b.y - s * 1.8, x1: b.x + s * 1.2, y1: b.y + s * 1, s0: s * 5.5, s1: s * 7, r0: -0.5, at: 180, ms: 380, a0: 1, a1: 0.3, ease: 'out' });
    O(OG.main, { ...at(b), s0: s * 10, s1: s * 11, flip: 8, at: 260, ms: 1150, a0: 1, a1: 0.85, ease: 'lin' });
    P({ sprite: 'flash', ...at(b), s0: s * 2, s1: s * 7, at: 300, ms: 320, a0: 1 });
    O(OG.p4, { ...at({ x: b.x, y: b.y + s * 0.4 }), s0: s * 1.5, s1: s * 9, at: 320, ms: 620, a0: 1, sy: 0.6 });
    O(OG.p3, { x0: b.x, y0: b.y - s * 0.8, x1: b.x, y1: b.y - s * 2.4, s0: s * 2.4, s1: s * 4.6, at: 420, ms: 820, a0: 1 });
    for (let i = 0; i < 4; i++) {
      const t = (i / 4) * Math.PI * 2 + 0.4;
      const c = { x: b.x + Math.cos(t) * s * 2, y: b.y + Math.sin(t) * s * 1.2 };
      O(OG.p5, { x0: c.x, y0: c.y - s * 0.6, x1: c.x, y1: c.y - s * 1.6, s0: s * 1.4, s1: s * 3.4, at: 400 + i * 70, ms: 620, a0: 1 });
    }
    O(OG.p6, { ...at(b), s0: s * 2, s1: s * 5, r0: 0, r1: 2, at: 360, ms: 900, a0: 0.9 });
    O(OG.p7, { ...at({ x: b.x, y: b.y - s * 0.5 }), s0: s * 3, s1: s * 5, at: 500, ms: 900, a0: 0.6, a1: 0 });
    spray(OG.p8, 8, 360, s * 3.4, 2, 900);
  } else if (kind === 'suzune') {
    // 鬼哭破城：絵のコマ送りに大槌の振り下ろしまで入っている（3コマ目あたりで地面に当たる）。地割れと鬼火、破片が飛び散る
    const ground = { x: b.x, y: b.y + s * 0.4 };
    const size = s * 10;
    O(OG.main, { x0: ground.x, y0: ground.y - size * 0.38, x1: ground.x, y1: ground.y - size * 0.4, s0: size, s1: size * 1.08, flip: 8, at: 120, ms: 1150, a0: 1, a1: 0.85, ease: 'lin' });
    hitAt = 400;
    P({ sprite: 'flash', ...at(b), s0: s * 2, s1: s * 7, at: 400, ms: 320, a0: 1 });
    O(OG.p1, { ...at(ground), s0: s * 3, s1: s * 7, at: 400, ms: 900, a0: 1, a1: 0.2 });
    O(OG.p4, { ...at(ground), s0: s * 1.5, s1: s * 9, at: 410, ms: 600, a0: 1 });
    for (let i = 0; i < 4; i++) {
      const t = (i / 4) * Math.PI * 2 + 0.4;
      const c = { x: ground.x + Math.cos(t) * s * 1.9, y: ground.y + Math.sin(t) * s * 1.1 };
      O(OG.p5, { x0: c.x, y0: c.y - s * 0.6, x1: c.x, y1: c.y - s * 1.4, s0: s * 1.4, s1: s * 3.4, at: 460 + i * 70, ms: 600, a0: 1 });
    }
    for (let i = 0; i < 12; i++) {
      const t = rnd(-Math.PI, 0);
      const d = s * rnd(2, 4);
      O(i % 2 ? OG.p2 : OG.p6, { x0: b.x, y0: b.y, x1: b.x + Math.cos(t) * d, y1: b.y + s * 1.5, arc: s * rnd(2, 3.5), s0: s * 0.9, s1: s * 0.7, r0: rnd(0, 6), r1: rnd(6, 14), at: 420 + rnd(0, 80), ms: 760, a0: 1, a1: 0.6, ease: 'lin' });
    }
    O(OG.p3, { x0: b.x, y0: b.y - s * 1, x1: b.x, y1: b.y - s * 2.8, s0: s * 2, s1: s * 4.2, at: 520, ms: 760, a0: 0.9 });
    for (let i = 0; i < 3; i++) O(OG.p7, { ...at({ x: b.x + rnd(-s * 2, s * 2), y: b.y - s * rnd(1, 2.5) }), s0: s * 2.5, s1: s * 3, r0: rnd(0, 6), at: 440 + i * 120, ms: 220, a0: 1 });
    spray(OG.p8, 6, 460, s * 3, 2.2, 900);
  } else {
    // 九尾一閃：巨大な三日月の斬撃、九本の尾の狐火が扇に広がり、狐火が戻って回復
    hitAt = 260;
    O(OG.p1, { x0: b.x - s * 1.5, y0: b.y - s * 1.5, x1: b.x + s * 1.2, y1: b.y + s * 1.2, s0: s * 5, s1: s * 6.5, r0: -0.6, at: 200, ms: 360, a0: 1, a1: 0.4, ease: 'out' });
    O(OG.main, { ...at(b), s0: s * 10, s1: s * 11, flip: 8, at: 240, ms: 1050, a0: 1, a1: 0.85, ease: 'lin' });
    P({ sprite: 'flash', ...at(b), s0: s * 1.5, s1: s * 6, at: 240, ms: 300, a0: 1 });
    for (let i = 0; i < 9; i++) {
      const t = -Math.PI / 2 + (i - 4) * 0.32;
      O(OG.p3, { x0: b.x, y0: b.y, x1: b.x + Math.cos(t) * s * 2.6, y1: b.y + Math.sin(t) * s * 2.6, s0: s * 1.2, s1: s * 2.8, r0: t + Math.PI / 2 - 0.6, at: 340 + Math.abs(i - 4) * 40, ms: 700, a0: 1, a1: 0 });
    }
    O(OG.p4, { ...at(b), s0: s * 1, s1: s * 7.5, at: 280, ms: 600, a0: 0.9, sy: 0.6 });
    spray(OG.p2, 10, 300, s * 3.6, 1.1, 800);
    O(OG.p5, { ...at(b), s0: s * 1, s1: s * 4, r0: 0.4, at: 240, ms: 300, a0: 1 });
    spray(OG.p8, 5, 360, s * 3, 2.2, 900);
    // 吸収：緑と青の光の玉が自分へ戻る
    for (let i = 0; i < 6; i++) O(OG.p6, { x0: b.x + rnd(-s, s), y0: b.y + rnd(-s, s), x1: a.x, y1: a.y, arc: s * rnd(0.8, 2), s0: s * 1, s1: s * 0.6, at: 700 + i * 60, ms: 520, a0: 1, a1: 0.4, ease: 'in' });
  }
  return { parts, hitAt };
}

/** プロローグの酒呑童子の術「紅蓮斬」：奥義の絵（ougi_shuten）の部品で小さめの紅蓮の斬撃。hitAt＝当たるまでの時間 */
export function shutenSkillRecipe(a: Pt, b: Pt, s: number, now: number, k: number): { parts: Particle[]; hitAt: number } {
  const parts: Particle[] = [];
  const sheet = 'ougi_shuten';
  const O = (cell: number, p: Omit<Particle, 't0' | 'ms' | 'sprite' | 'sheet' | 'cell'> & { at?: number; ms: number }) => parts.push({ sprite: 'flash', sheet, cell, ...p, t0: now + (p.at ?? 0) * k, ms: p.ms * k });
  const at = (pt: Pt) => ({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
  const dir = Math.atan2(b.y - a.y, b.x - a.x);
  O(OG.p1, { x0: b.x - s * 1.1, y0: b.y - s * 0.9, x1: b.x + s * 0.6, y1: b.y + s * 0.5, s0: s * 2.6, s1: s * 3.4, r0: -0.5, at: 80, ms: 300, a0: 1, a1: 0.2, ease: 'out' });
  O(OG.main, { ...at(b), s0: s * 3.6, s1: s * 4, flip: 8, at: 160, ms: 700, a0: 1, a1: 0.8, ease: 'lin' });
  O(OG.p4, { ...at(b), s0: s * 0.8, s1: s * 3.6, at: 200, ms: 420, a0: 0.9, sy: 0.6 });
  for (let i = 0; i < 6; i++) {
    const t = dir + rnd(-1, 1);
    const d = s * rnd(1, 1.8);
    O(OG.p8, { x0: b.x, y0: b.y, x1: b.x + Math.cos(t) * d, y1: b.y + Math.sin(t) * d * 0.8, s0: s * 0.9, s1: s * 0.5, r0: rnd(0, 6), at: 200 + rnd(0, 60), ms: 520, a0: 1 });
  }
  return { parts, hitAt: 200 };
}

// ---------------- 術（自分を中心に放つもの）：fx-jutsu.webp（4×4。art-src/gen-fx-jutsu.sh の並び） ----------------
const JF = { okuriRing: 0, ember: 1, lantern: 2, lanternCircle: 3, afterimage: 4, cross: 5, tornado: 6, windBall: 7, dust: 8, spark: 9, mend: 10, shield: 11, foxRing: 12, foxfire: 13, crescent: 14, moonSlash: 15 };

/**
 * 術のエフェクト。a＝放つ側、cells＝効く範囲のマスの中心、at＝範囲の中心（送り火は選んだマス）、s＝ヘックスの半径、k＝時間の倍率。
 * ougi＝奥義版（大きく・長く）。hitAt＝効く瞬間までの時間
 */
export function castRecipe(jutsu: string, a: Pt, at: Pt, cells: Pt[], s: number, now: number, k: number, ougi: boolean): { parts: Particle[]; hitAt: number } {
  const parts: Particle[] = [];
  const big = ougi ? 1.5 : 1;
  const O = (cell: number, p: Omit<Particle, 't0' | 'ms' | 'sprite' | 'sheet' | 'cell'> & { at?: number; ms: number }) =>
    parts.push({ sprite: 'flash', sheet: 'fxJutsu', cell, ...p, t0: now + (p.at ?? 0) * k, ms: p.ms * k });
  const P = (p: Omit<Particle, 't0' | 'ms'> & { at?: number; ms: number }) => parts.push({ ...p, t0: now + (p.at ?? 0) * k, ms: p.ms * k });
  const still = (pt: Pt) => ({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
  // 溜め：放つ側に光が集まる
  P({ sprite: 'ring', ...still(a), s0: s * 2.4, s1: s * 0.4, ms: 220, a0: 0.2, a1: 0.9, ease: 'in' });
  let hitAt = 240;
  switch (jutsu) {
    case 'okuribi':
      // 送り火：狙ったマスへ火の玉が飛び、炎の環が広がって、範囲のマスから火の粉が上がる
      O(JF.ember, { x0: a.x, y0: a.y, x1: at.x, y1: at.y, arc: s * 2, s0: s * 1.2, s1: s * 1.6, along: true, at: 160, ms: 320, a0: 1, ease: 'in' });
      hitAt = 480;
      O(JF.okuriRing, { ...still(at), s0: s * 1.2, s1: s * 3.6 * big, at: 460, ms: 620, a0: 1, sy: 0.7 });
      for (const c of cells) O(JF.ember, { x0: c.x, y0: c.y, x1: c.x, y1: c.y - s * 1.2, s0: s * 0.9, s1: s * 1.3, at: 500 + rnd(0, 160), ms: 640, a0: 1 });
      P({ sprite: 'flash', ...still(at), s0: s * 1.5, s1: s * 4 * big, at: 460, ms: 300, a0: 1 });
      break;
    case 'toro':
      // 灯籠陣：足元に光の陣が開き、灯籠が立つ
      O(JF.lanternCircle, { ...still({ x: a.x, y: a.y + s * 0.3 }), s0: s * 1, s1: s * 3.4 * big, at: 120, ms: 900, a0: 1, a1: 0.6, sy: 0.55, r0: 0, r1: 0.6 });
      for (const c of cells.slice(0, ougi ? 6 : 3)) O(JF.lantern, { x0: c.x, y0: c.y + s * 0.4, x1: c.x, y1: c.y - s * 0.1, s0: s * 0.6, s1: s * 1.3, at: 260 + rnd(0, 200), ms: 900, a0: 1, a1: 0, fadeIn: 0.2 });
      break;
    case 'shukuchi':
      // 縮地：残像を残して跳び、着地点で十字に斬る
      O(JF.afterimage, { x0: a.x, y0: a.y, x1: at.x, y1: at.y, s0: s * 2.4, s1: s * 2.8, along: true, ms: 260, a0: 1, a1: 0.2, ease: 'lin' });
      hitAt = 300;
      O(JF.cross, { ...still(at), s0: s * 1.6, s1: s * 3 * big, r0: rnd(-0.3, 0.3), at: 260, ms: 420, a0: 1 });
      P({ sprite: 'flash', ...still(at), s0: s * 1.2, s1: s * 3.5, at: 260, ms: 260, a0: 1 });
      break;
    case 'tsumuji':
      // つむじ風：竜巻が巻き上がり、風の塊が周りへ吹き出す
      O(JF.tornado, { x0: a.x, y0: a.y + s * 0.2, x1: a.x, y1: a.y - s * 0.3, s0: s * 1.6, s1: s * 3.4 * big, at: 120, ms: 820, a0: 1, a1: 0.3 });
      hitAt = 360;
      for (const c of cells) if (c.x !== a.x || c.y !== a.y) O(JF.windBall, { x0: a.x, y0: a.y, x1: c.x + (c.x - a.x) * 0.8, y1: c.y + (c.y - a.y) * 0.8, s0: s * 0.9, s1: s * 1.5, r0: 0, r1: 4, at: 300, ms: 520, a0: 1 });
      break;
    case 'toppu':
      // 突貫普請：土煙と金槌の火花が周りで一斉に上がる
      for (const c of cells) {
        O(JF.dust, { x0: c.x, y0: c.y + s * 0.3, x1: c.x, y1: c.y - s * 0.2, s0: s * 1, s1: s * 1.9, at: 120 + rnd(0, 200), ms: 700, a0: 0.9 });
        O(JF.spark, { ...still(c), s0: s * 0.6, s1: s * 1.5, at: 200 + rnd(0, 240), ms: 360, a0: 1 });
      }
      hitAt = 320;
      break;
    case 'shuzen':
      // 鬼の修繕：緑と金の光が昇り、鬼の守りの盾が立つ
      O(JF.shield, { ...still(a), s0: s * 1.2, s1: s * 2.2 * big, at: 120, ms: 820, a0: 0.95, a1: 0 });
      for (const c of cells.slice(0, ougi ? 12 : 6)) O(JF.mend, { x0: c.x, y0: c.y + s * 0.3, x1: c.x, y1: c.y - s * 0.6, s0: s * 0.8, s1: s * 1.2, at: 200 + rnd(0, 240), ms: 760, a0: 1 });
      break;
    case 'kitsunebijin':
      // 狐火の陣：青い狐火の環が広がり、範囲のマスに狐火が灯る
      O(JF.foxRing, { ...still(a), s0: s * 1, s1: s * 4.2 * big, at: 120, ms: 900, a0: 1, a1: 0.4, sy: 0.6 });
      hitAt = 420;
      for (const c of cells) O(JF.foxfire, { x0: c.x, y0: c.y + s * 0.2, x1: c.x, y1: c.y - s * 0.3, s0: s * 0.5, s1: s * 1, at: 360 + rnd(0, 220), ms: 760, a0: 1 });
      break;
    case 'gekka':
      // 月下の構え：三日月の光をまとい、月の斬り返しが閃く
      O(JF.crescent, { ...still(a), s0: s * 1.4, s1: s * 2.6 * big, r0: -0.4, r1: 0.2, at: 80, ms: 900, a0: 1, a1: 0.2 });
      O(JF.moonSlash, { ...still(a), s0: s * 1, s1: s * 2.2, r0: 0.5, at: 320, ms: 380, a0: 1 });
      break;
  }
  return { parts, hitAt };
}
